// What the AI read of an uploaded document should pull out of it, per document
// type (PET-4000). Pure data, so the worker builds the model's output schema from
// it and the web apps render the same fields for review without importing any
// model code.

import { canonicalDocumentSlug } from './fleet-documents'
import { ISO_DATE_REGEX } from './time'

// The four tables a document version can live in. An extraction row points at
// one of them by `(store, documentId)`.
export const DOCUMENT_EXTRACTION_STORES = ['onboarding', 'driver', 'truck', 'carrier'] as const
export type DocumentExtractionStore = (typeof DOCUMENT_EXTRACTION_STORES)[number]

export const DOCUMENT_EXTRACTION_STATUSES = ['pending', 'running', 'succeeded', 'failed'] as const
export type DocumentExtractionStatus = (typeof DOCUMENT_EXTRACTION_STATUSES)[number]

// Who put the value in a document's `expiration_date` (DOC-47, DOC-48). Only an
// `extracted` date is still the model's word: the office has not looked at it.
export const EXPIRATION_DATE_SOURCES = ['manual', 'extracted', 'confirmed'] as const
export type ExpirationDateSource = (typeof EXPIRATION_DATE_SOURCES)[number]

// The expiry columns for a date a person typed, or cleared.
export const manualExpiry = (
  expirationDate: string | null,
): { expirationDate: string | null; expirationDateSource: ExpirationDateSource | null } => ({
  expirationDate,
  expirationDateSource: expirationDate ? 'manual' : null,
})

// `date` is an ISO YYYY-MM-DD string, `money` a whole-dollar amount, `last4` the
// last four digits of a number we refuse to store whole (DOC-50).
export const DOCUMENT_EXTRACTION_FIELD_KINDS = [
  'text',
  'date',
  'money',
  'number',
  'boolean',
  'textList',
  'last4',
] as const
export type DocumentExtractionFieldKind = (typeof DOCUMENT_EXTRACTION_FIELD_KINDS)[number]

export type DocumentExtractionField = {
  key: string
  kind: DocumentExtractionFieldKind
  // Told to the model. English, and about the paper, not the UI: labels come
  // from the `documents` locale namespace under `extraction.fields.<key>`.
  hint: string
  // Whether a tenant that never touched this field gets it read (DOC-56).
  // Absent means on.
  defaultEnabled?: boolean
  // Always read, whatever the tenant ticks (DOC-54): the date the document's
  // expiry comes from (DOC-47), printed or derived.
  locked?: true
}

export type DocumentExtractionTemplate = {
  // Canonical slug (fleet vocabulary), or `generic` for the fallback.
  slug: string
  // Bumped whenever the catalog or the wording change, so a stored read says
  // which questions it answered.
  version: number
  // One line on what the paper is, for the prompt.
  describes: string
  fields: readonly DocumentExtractionField[]
  // Which field feeds the document's `expiration_date`, if any.
  expirationField: string | null
  // A document with no printed expiry that expires a fixed time after a printed
  // date (an annual DOT inspection). Computed in code, never asked of the model.
  derivedExpiration?: { fromField: string; months: number }
}

// The two dates of a type outside the reading settings: when it took effect and
// when it lapses. The hints tell the model which printed date counts for each
// type. Locked, since nothing else lets the office choose them.
const datesOf = (hints: { effective: string; expiration: string }) =>
  [
    { key: 'effectiveDate', kind: 'date', hint: hints.effective, locked: true },
    { key: 'expirationDate', kind: 'date', hint: hints.expiration, locked: true },
  ] as const satisfies readonly DocumentExtractionField[]

// Any document may carry an expiry, even a type that usually prints none: the
// read leaves it null then, and the office can still type one.
const NONE_PRINTED = 'Expiration date, only if one is printed.'

// A type in the reading settings reads only the dates its paper prints
// (DOC-54). The one its expiry comes from is locked; any other is a field like
// the rest.
const expiryOf = (hint: string) =>
  ({ key: 'expirationDate', kind: 'date', hint, locked: true }) as const

// A status the read reduces to one of two words, so a mismatch warning can
// test it without guessing at the paper's wording (DOC-57).
export const ACTIVE_STATUS = 'ACTIVE'
export const INSPECTION_FAILED = 'FAIL'
const ACTIVE_OR_INACTIVE = 'Return ACTIVE when the status is active, otherwise INACTIVE.'

const VIN_HINT = 'Vehicle identification number (VIN), 17 characters, exactly as printed.'
const PLATE_HINT = 'License plate number, exactly as printed.'

export const DOCUMENT_EXTRACTION_TEMPLATES: Readonly<Record<string, DocumentExtractionTemplate>> = {
  insurance_certificate: {
    slug: 'insurance_certificate',
    version: 2,
    describes:
      'a certificate of liability insurance (usually an ACORD 25) for a trucking company or owner operator',
    // The auto liability policy is what a truck is dispatched under; the
    // form's other lines of coverage often run on different dates.
    fields: datesOf({
      effective: 'Effective date of the automobile liability policy.',
      expiration: 'Expiration date of the automobile liability policy.',
    }),
    expirationField: 'expirationDate',
  },
  workers_comp_certificate: {
    slug: 'workers_comp_certificate',
    version: 4,
    describes: "a certificate of workers' compensation insurance",
    fields: [
      {
        key: 'effectiveDate',
        kind: 'date',
        hint: "Effective date of the workers' compensation policy.",
      },
      expiryOf("Expiration date of the workers' compensation policy."),
      { key: 'insuredName', kind: 'text', hint: 'Name of the insured individual or company.' },
      {
        key: 'documentKind',
        kind: 'text',
        hint: 'Return POLICY for a workers compensation policy or certificate of insurance, EXEMPTION for an exemption certificate.',
      },
      {
        key: 'policyOrCertificateNumber',
        kind: 'text',
        hint: 'Policy number, or exemption certificate number.',
      },
      {
        key: 'issuer',
        kind: 'text',
        hint: 'Insurance company or issuing authority, if printed.',
      },
    ],
    expirationField: 'expirationDate',
  },
  workers_comp_exemption: {
    slug: 'workers_comp_exemption',
    version: 4,
    describes: "a workers' compensation exemption certificate",
    fields: [
      { key: 'effectiveDate', kind: 'date', hint: 'Date the exemption takes effect.' },
      expiryOf('Date the exemption expires.'),
      { key: 'insuredName', kind: 'text', hint: 'Name of the exempt individual or company.' },
      {
        key: 'documentKind',
        kind: 'text',
        hint: 'Return POLICY for a workers compensation policy or certificate of insurance, EXEMPTION for an exemption certificate.',
      },
      {
        key: 'policyOrCertificateNumber',
        kind: 'text',
        hint: 'Exemption certificate number, or policy number.',
      },
      { key: 'issuer', kind: 'text', hint: 'Issuing authority or insurance company, if printed.' },
    ],
    expirationField: 'expirationDate',
  },
  cdl: {
    slug: 'cdl',
    version: 4,
    describes: "a US commercial driver's license (front and back may be separate images)",
    fields: [
      expiryOf('Expiration date of the license.'),
      { key: 'fullName', kind: 'text', hint: "Driver's full name, as printed." },
      { key: 'licenseNumber', kind: 'text', hint: 'License number (DL / CDL number).' },
      { key: 'issuingState', kind: 'text', hint: 'Two-letter code of the issuing state.' },
      { key: 'dateOfBirth', kind: 'date', hint: 'Date of birth (DOB).' },
      { key: 'cdlClass', kind: 'text', hint: 'License class: A, B or C.' },
      {
        key: 'restrictions',
        kind: 'textList',
        hint: 'Restriction codes, one per item. Empty when NONE.',
      },
      {
        key: 'endorsements',
        kind: 'textList',
        hint: 'Endorsement codes (H, N, P, S, T, X...), one per item. Empty when NONE.',
      },
    ],
    expirationField: 'expirationDate',
  },
  medical_card: {
    slug: 'medical_card',
    version: 2,
    describes: "a DOT medical examiner's certificate (medical card)",
    fields: datesOf({
      effective: 'Date the medical examination was performed.',
      expiration: 'Date the medical certificate expires.',
    }),
    expirationField: 'expirationDate',
  },
  registration: {
    slug: 'registration',
    version: 4,
    describes: 'a vehicle registration card',
    fields: [
      expiryOf('Expiration date of the registration.'),
      { key: 'ownerName', kind: 'text', hint: "Registered owner's full name or company name." },
      { key: 'vin', kind: 'text', hint: VIN_HINT },
      { key: 'plateNumber', kind: 'text', hint: PLATE_HINT },
      { key: 'registrationState', kind: 'text', hint: 'Two-letter code of the registering state.' },
      { key: 'vehicleYear', kind: 'number', hint: 'Model year of the vehicle.' },
      { key: 'vehicleMake', kind: 'text', hint: 'Make of the vehicle.' },
      { key: 'bodyType', kind: 'text', hint: 'Vehicle type or body type, if printed.' },
    ],
    expirationField: 'expirationDate',
  },
  dot_inspection: {
    slug: 'dot_inspection',
    version: 4,
    describes: 'an annual DOT vehicle inspection report',
    fields: [
      {
        key: 'inspectionDate',
        kind: 'date',
        hint: 'Date the inspection was performed.',
        locked: true,
      },
      expiryOf(NONE_PRINTED),
      { key: 'vin', kind: 'text', hint: VIN_HINT },
      { key: 'plateNumber', kind: 'text', hint: `${PLATE_HINT} Null when not shown.` },
      { key: 'vehicleYear', kind: 'number', hint: 'Model year of the vehicle, if shown.' },
      { key: 'vehicleMake', kind: 'text', hint: 'Make of the vehicle, if shown.' },
      {
        key: 'inspectionResult',
        kind: 'text',
        hint: 'Return PASS when the vehicle passed the inspection, FAIL when it did not.',
      },
      { key: 'inspectorName', kind: 'text', hint: "Inspector's full name." },
      { key: 'inspectorSigned', kind: 'boolean', hint: 'Whether the inspector signed the report.' },
      {
        key: 'inspectionLocation',
        kind: 'text',
        hint: 'Inspection company or location.',
      },
    ],
    expirationField: 'expirationDate',
    // An annual inspection is good for 12 months (49 CFR 396.17) and the
    // report rarely prints an expiry of its own.
    derivedExpiration: { fromField: 'inspectionDate', months: 12 },
  },
  w9: {
    slug: 'w9',
    version: 5,
    describes: 'an IRS Form W-9',
    fields: [
      { key: 'name', kind: 'text', hint: 'Name on line 1.' },
      {
        key: 'businessName',
        kind: 'text',
        hint: 'Business name or disregarded entity name on line 2, if any.',
      },
      {
        key: 'taxClassification',
        kind: 'text',
        hint: 'Federal tax classification that is checked (individual/sole proprietor, C corporation, S corporation, partnership, trust/estate, LLC with its tax letter, other).',
      },
      {
        key: 'tinLast4',
        kind: 'last4',
        hint: 'Last 4 digits of the taxpayer identification number (EIN or SSN).',
      },
      { key: 'address', kind: 'text', hint: 'Address, city, state and ZIP code.' },
      { key: 'signed', kind: 'boolean', hint: 'Whether the form is signed.' },
      { key: 'signatureDate', kind: 'date', hint: 'Date the form was signed, if provided.' },
    ],
    // A W-9 does not lapse.
    expirationField: null,
  },
  voided_check: {
    slug: 'voided_check',
    version: 3,
    describes: 'a voided bank check used to set up payments',
    fields: datesOf({
      effective: 'Date printed or written on the check.',
      expiration: NONE_PRINTED,
    }),
    expirationField: 'expirationDate',
  },
  sunbiz_filing: {
    slug: 'sunbiz_filing',
    version: 5,
    describes: 'a Florida Division of Corporations (Sunbiz) entity detail page',
    fields: [
      { key: 'legalName', kind: 'text', hint: 'Legal business name of the entity.' },
      {
        key: 'feinLast4',
        kind: 'last4',
        hint: 'Last 4 digits of the FEI/EIN number.',
      },
      { key: 'companyStatus', kind: 'text', hint: `Entity status. ${ACTIVE_OR_INACTIVE}` },
      { key: 'principalAddress', kind: 'text', hint: 'Principal address.' },
      { key: 'registeredAgent', kind: 'text', hint: 'Registered agent name.' },
      {
        key: 'officers',
        kind: 'textList',
        hint: 'Authorized persons, members, managers or officers, one per item, as "Title Name".',
      },
    ],
    // A filing and a snapshot describe the company on a day; neither lapses.
    expirationField: null,
  },
  safer_snapshot: {
    slug: 'safer_snapshot',
    version: 5,
    describes: 'an FMCSA SAFER company snapshot',
    fields: [
      { key: 'legalName', kind: 'text', hint: 'Legal name of the company.' },
      { key: 'usdotNumber', kind: 'text', hint: 'USDOT number, digits only.' },
      { key: 'usdotStatus', kind: 'text', hint: `USDOT status. ${ACTIVE_OR_INACTIVE}` },
      {
        key: 'operatingAuthorityStatus',
        kind: 'text',
        hint: 'Operating authority status, exactly as printed. Null when not applicable.',
      },
      { key: 'physicalAddress', kind: 'text', hint: 'Physical address.' },
      {
        key: 'crashes',
        kind: 'number',
        hint: 'Total number of crashes in the United States crash table (24 months).',
      },
      {
        key: 'safetyRating',
        kind: 'text',
        hint: 'Carrier safety rating (Satisfactory, Conditional, Unsatisfactory, or None).',
      },
    ],
    expirationField: null,
  },
}

// Anything without a template of its own, tenant custom types included.
export const GENERIC_DOCUMENT_EXTRACTION_TEMPLATE: DocumentExtractionTemplate = {
  slug: 'generic',
  version: 2,
  describes: 'a compliance document submitted by a trucking company or driver',
  fields: datesOf({
    effective: 'Date the document takes effect or was issued.',
    expiration: 'Date the document expires. Null when no expiry is printed.',
  }),
  expirationField: 'expirationDate',
}

// Our own output (packets, archives, signed agreements): nothing to read back.
const NOT_EXTRACTED_SLUGS: ReadonlySet<string> = new Set([
  'broker_mc',
  'carrier_docs',
  'driver_docs',
  'signed_agreement',
])

export function isExtractableDocumentSlug(slug: string): boolean {
  return !NOT_EXTRACTED_SLUGS.has(slug)
}

export type DocumentExtractionValue = string | number | boolean | string[] | null

// The model's read of one document version. `values` is keyed by the template's
// field keys; every value is nullable because an absent or illegible field must
// come back null, not guessed.
export type DocumentExtractionFields = {
  values: Record<string, DocumentExtractionValue>
  detectedDocumentType: string | null
  matchesExpectedType: boolean | null
  legible: boolean | null
  lowConfidenceFields: string[]
  notes: string
}

// The first three are written by the worker with the read. The last three are
// never stored: they compare the read with the application as it stands now,
// so they follow the applicant's corrections (DOC-57).
export const DOCUMENT_EXTRACTION_WARNING_CODES = [
  'type_mismatch',
  'illegible',
  'already_expired',
  'field_mismatch',
  'inactive_status',
  'inspection_failed',
] as const
export type DocumentExtractionWarningCode = (typeof DOCUMENT_EXTRACTION_WARNING_CODES)[number]

export type DocumentExtractionWarning = {
  code: DocumentExtractionWarningCode
  // The template field the warning is about, for the live ones.
  field?: string
  expected?: string
  found?: string
}

/**
 * One value as it may be stored, whoever wrote it: the model or a reviewer. A
 * date the reviewer can't parse is no better than none, and for a `last4` field
 * only the last four digits survive, whatever came in (DOC-50).
 */
export function normalizeExtractionValue(
  kind: DocumentExtractionFieldKind,
  value: unknown,
): DocumentExtractionValue {
  switch (kind) {
    case 'date':
      return typeof value === 'string' && ISO_DATE_REGEX.test(value) ? value : null
    case 'money':
    case 'number':
      return typeof value === 'number' && Number.isFinite(value) ? value : null
    case 'boolean':
      return typeof value === 'boolean' ? value : null
    case 'textList':
      return Array.isArray(value) ? value.filter((item) => typeof item === 'string') : []
    case 'last4': {
      const digits = (typeof value === 'string' ? value : '').replace(/\D/g, '')
      return digits.length >= 4 ? digits.slice(-4) : null
    }
    case 'text':
      return typeof value === 'string' && value.trim() !== '' ? value.trim() : null
  }
}

// What a reviewer sent, reduced to the template's own fields, each normalized.
// Keys the template does not ask for are dropped, never stored.
export function normalizeReviewedValues(
  template: DocumentExtractionTemplate,
  values: Readonly<Record<string, unknown>>,
): Record<string, DocumentExtractionValue> {
  return Object.fromEntries(
    template.fields
      .filter((field) => field.key in values)
      .map((field) => [field.key, normalizeExtractionValue(field.kind, values[field.key])]),
  )
}

export function documentExtractionTemplateFor(slug: string): DocumentExtractionTemplate {
  return (
    DOCUMENT_EXTRACTION_TEMPLATES[canonicalDocumentSlug(slug)] ??
    GENERIC_DOCUMENT_EXTRACTION_TEMPLATE
  )
}

// The date a set of values puts on the document, when the template has one to
// give. Shared by the worker (the read) and the approval (the reviewed read), so
// both always agree on which field the document's expiry comes from.
export function expirationDateFromValues(
  template: DocumentExtractionTemplate,
  values: Readonly<Record<string, unknown>>,
): string | null {
  if (!template.expirationField) return null
  const value = normalizeExtractionValue('date', values[template.expirationField])
  return typeof value === 'string' ? value : null
}

// One tenant's choice for each field it changed, keyed by field key. A field
// with no entry follows its `defaultEnabled` (DOC-56); a locked one is always
// read (DOC-54).
export type DocumentExtractionFieldToggles = Readonly<Record<string, boolean>>

export const isExtractionFieldEnabled = (
  field: DocumentExtractionField,
  toggles: DocumentExtractionFieldToggles,
): boolean => field.locked === true || (toggles[field.key] ?? field.defaultEnabled ?? true)

// The template a tenant's read asks the model for: the same slug and version,
// narrowed to the fields that tenant has on (DOC-55).
export function resolveTemplateFields(
  template: DocumentExtractionTemplate,
  toggles: DocumentExtractionFieldToggles,
): DocumentExtractionTemplate {
  return {
    ...template,
    fields: template.fields.filter((field) => isExtractionFieldEnabled(field, toggles)),
  }
}

// The fields a stored read answered, so a read made before a tenant changed its
// settings keeps showing what it asked for (DOC-55). With nothing read yet, only
// the locked fields, which every read asks for.
export function fieldsOfRead(
  template: DocumentExtractionTemplate,
  values: Readonly<Record<string, unknown>> | null | undefined,
): DocumentExtractionTemplate {
  const asked = values ? new Set(Object.keys(values)) : null
  return {
    ...template,
    fields: template.fields.filter((field) =>
      asked ? asked.has(field.key) || field.locked === true : field.locked === true,
    ),
  }
}

/**
 * What the application says about the document's subject, for the live
 * mismatch warnings (DOC-57). Every value is optional: a check runs only when
 * both the read and the application have something to compare.
 */
export type DocumentExtractionReference = {
  legalName?: string | null
  usdotNumber?: string | null
  // Compared, and shown in a warning, by its last 4 digits only (DOC-50).
  ein?: string | null
  vin?: string | null
  plateNumber?: string | null
  driverName?: string | null
  licenseNumber?: string | null
}

// Letters and digits only, upper-cased: "1ftfw-1et 5d" and "1FTFW1ET5D" agree.
const comparableCode = (value: string): string => value.toUpperCase().replace(/[^A-Z0-9]/g, '')

// A legal entity's suffix is printed every which way across documents.
const ENTITY_SUFFIXES = new Set([
  'LLC',
  'LC',
  'INC',
  'INCORPORATED',
  'CORP',
  'CORPORATION',
  'CO',
  'COMPANY',
  'LTD',
  'LP',
  'LLP',
  'PA',
  'PLLC',
])

export const comparableName = (value: string): string =>
  value
    .toUpperCase()
    .replace(/&/g, ' AND ')
    .replace(/\./g, '')
    .replace(/[^A-Z0-9 ]/g, ' ')
    .split(/\s+/)
    .filter((word) => word !== '' && !ENTITY_SUFFIXES.has(word))
    .join(' ')

// A person's name reads "LAST, FIRST MIDDLE" on a license and "First Last" in
// the application: equal when every word of the application's name is on the
// paper.
const personNamesAgree = (read: string, expected: string): boolean => {
  const onPaper = new Set(comparableName(read).split(' '))
  const words = comparableName(expected).split(' ').filter(Boolean)
  return words.length > 0 && words.every((word) => onPaper.has(word))
}

const last4Of = (value: string): string | null => {
  const digits = value.replace(/\D/g, '')
  return digits.length >= 4 ? digits.slice(-4) : null
}

type Comparison = {
  field: string
  reference: keyof DocumentExtractionReference
  agree: (read: string, expected: string) => boolean
  // Another field that may carry the same value instead: a W-9's line 1 is the
  // owner for a sole proprietor, whose business name is then on line 2.
  orField?: string
}

const sameCode = (read: string, expected: string) =>
  comparableCode(read) === comparableCode(expected)
const sameName = (read: string, expected: string) =>
  comparableName(read) === comparableName(expected)
const sameLast4 = (read: string, expected: string) => last4Of(read) === last4Of(expected)

const COMPARISONS: readonly Comparison[] = [
  { field: 'legalName', reference: 'legalName', agree: sameName },
  { field: 'name', reference: 'legalName', agree: sameName, orField: 'businessName' },
  { field: 'usdotNumber', reference: 'usdotNumber', agree: sameCode },
  { field: 'feinLast4', reference: 'ein', agree: sameLast4 },
  { field: 'tinLast4', reference: 'ein', agree: sameLast4 },
  { field: 'vin', reference: 'vin', agree: sameCode },
  { field: 'plateNumber', reference: 'plateNumber', agree: sameCode },
  { field: 'fullName', reference: 'driverName', agree: personNamesAgree },
  { field: 'licenseNumber', reference: 'licenseNumber', agree: sameCode },
]

const STATUS_FIELDS = ['usdotStatus', 'companyStatus'] as const

const textOf = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() !== '' ? value.trim() : null

/**
 * Advisory warnings from what a read says (DOC-57): an inactive entity, a failed
 * inspection, and, given the application's values, every read field that
 * contradicts them. Never stored and never gating: the reviewer decides
 * (DOC-46). `values` is what the office sees, the reviewed read over the model's.
 */
export function liveExtractionWarnings(
  template: DocumentExtractionTemplate,
  values: Readonly<Record<string, unknown>>,
  reference: DocumentExtractionReference = {},
): DocumentExtractionWarning[] {
  const asked = new Set(template.fields.map((field) => field.key))
  const warnings: DocumentExtractionWarning[] = []
  for (const field of STATUS_FIELDS) {
    const read = asked.has(field) ? textOf(values[field]) : null
    if (read && read.toUpperCase() !== ACTIVE_STATUS) {
      warnings.push({ code: 'inactive_status', field, found: read })
    }
  }
  const result = asked.has('inspectionResult') ? textOf(values.inspectionResult) : null
  if (result?.toUpperCase() === INSPECTION_FAILED) {
    warnings.push({ code: 'inspection_failed', field: 'inspectionResult' })
  }
  for (const comparison of COMPARISONS) {
    if (!asked.has(comparison.field)) continue
    const read = textOf(values[comparison.field])
    const expected = textOf(reference[comparison.reference])
    const alternative = comparison.orField ? textOf(values[comparison.orField]) : null
    if (alternative && expected && comparison.agree(alternative, expected)) continue
    if (read && expected && !comparison.agree(read, expected)) {
      // A last-4 field shows only its last four digits, either side (DOC-50).
      const shown = comparison.reference === 'ein' ? last4Of(expected) : expected
      warnings.push({
        code: 'field_mismatch',
        field: comparison.field,
        found: read,
        ...(shown ? { expected: shown } : {}),
      })
    }
  }
  return warnings
}

// The document types the reading settings list, by who the paper is about
// (DOC-55). Every other type reads only its two locked dates.
export const DOCUMENT_READING_GROUPS = [
  { key: 'company', slugs: ['safer_snapshot', 'sunbiz_filing', 'w9'] },
  { key: 'driver', slugs: ['cdl', 'workers_comp_certificate', 'workers_comp_exemption'] },
  { key: 'truck', slugs: ['registration', 'dot_inspection'] },
] as const

export const DOCUMENT_READING_SLUGS: readonly string[] = DOCUMENT_READING_GROUPS.flatMap(
  (group) => group.slugs,
)
